import type { RoomObject, Seat } from '@cineamo/ui/src/seat-map/typedefs';

import { findRowPairs, type PairKind } from '../../../api/models/seat/seat-pairs.ts';
import { normalizeObjectProfile } from '../room-3d/outline/room-geometry.ts';
import type { ObjectProfile, RoomGeometry } from '../room-3d/outline/types.ts';
import type { SeatStyle } from '../room-3d/room-design.ts';
import { fallbackScreen as designedFallbackScreen, screenWallWidth } from '../room-3d/screen-fallback.ts';
import { seatEndPanels } from '../room-3d/seat-ends.ts';
import { fullFormKey, type SeatForm, seatLookOfForm } from '../room-3d/seat-forms.ts';
import { bedBridgeParts, furnitureParts, type FurnitureKind } from '../room-3d/seat-kinds.ts';
import {
    type SeatEyeOffset,
    type SeatLook,
    seatEyeOffset,
    seatOptionsFor,
    seatStyleBridgeParts,
    seatStyleModel
} from '../room-3d/seat-styles.ts';
import { resolveSeatRise } from '../room-3d/tiers.ts';

// Ported from the mobile app's SeatMap3D.tsx (cineamo-app, CIN-12939). Plan space: millimeters,
// x to the right, y toward the screen (the front row has the largest y), z up, rotation.z in
// degrees clockwise as the 2D canvas draws it. World space: meters, three.js axes, seats face -z.
// This module has only type imports (and relative .ts ones) so node --test can load it without an install.

export type Vec3 = { x: number; y: number; z: number };
export type Box = {
    center: Vec3;
    size: Vec3;
    /** Rotation about the part's own x axis in radians, for slanted parts (a bed's hood, a beanbag's back). */
    tiltX?: number;
    /**
     * The part is the hard shell of a two-tone seat (drawn in the design's `seatShell` surface, not in the status or
     * category color). Absent = the cushion, the seat's own color.
     */
    material?: 'shell';
};
/** A wheel resting on the seat's base; `caster` marks a small front wheel, the others are drive wheels. */
export type Wheel = { center: Vec3; radius: number; caster?: true };

export type SeatNode = {
    seatId: Seat['id'];
    kind: 'seat' | 'wheelchair' | FurnitureKind;
    isSelectable: boolean;
    /** D-BOX motion seat: marked with a glowing strip on its front foot. */
    isDbox?: boolean;
    position: Vec3;
    rotationY: number;
    size: Vec3;
    parts: Box[];
    wheels: Wheel[];
    /** Underglow strips (beds) and lit cupholder rings (recliners), drawn in the room's accent color. */
    glows?: Box[];
    /** A reclined recliner: the eye is lower and further back (see `seatEyeOffset`). */
    eyeOffset?: SeatEyeOffset;
    /** How this seat is drawn (style, edge softness, details); absent for the plain map, which has one fixed seat. */
    form?: SeatForm;
};

export type BridgeNode = {
    leftSeatId: Seat['id'];
    rightSeatId: Seat['id'];
    position: Vec3;
    rotationY: number;
    parts: Box[];
    glows?: Box[];
    /** The form of the left half, which owns the bridge. */
    form?: SeatForm;
};

export type ScreenNode = {
    center: Vec3;
    size: Vec3;
    /** The screen object's profile with a bezel (CIN-15200) or a curve (CIN-15402); the map draws them from it. */
    profile?: ObjectProfile;
};

export type SceneLayout = {
    seats: SeatNode[];
    bridges: BridgeNode[];
    screen: ScreenNode;
    bounds: { min: Vec3; max: Vec3; center: Vec3 };
    groundY: number;
};

export const GROUND_Y = -0.5;
const PEDESTAL_BASE = 0.15;
const MAX_TIER_HEIGHT = 1;
/** Beyond this z range (mm) heights are imported garbage, not real tiers. */
const MAX_PLAUSIBLE_RISE_MM = 4000;
/** Plan space is millimeters, the 3D world is meters. */
export const MM = 1000;

export const NON_SELECTABLE_SEAT_TYPES: ReadonlySet<string> = new Set(['wheelchair', 'disabled']);
const WHEELCHAIR_TYPES: ReadonlySet<string> = new Set(['wheelchair']);
const FURNITURE_KINDS: Record<string, FurnitureKind> = { bed_left: 'bed', bed_right: 'bed', beanbag: 'beanbag' };

/** The furniture a seat type stands for, `null` for chairs (drawn in the seat style) and wheelchair spaces. */
export function furnitureKindOf(type: string | null | undefined): FurnitureKind | null {
    return (type && FURNITURE_KINDS[type]) || null;
}

const vec = (x: number, y: number, z: number): Vec3 => ({ x, y, z });
const box = (center: Vec3, size: Vec3): Box => ({ center, size });

function baseAndBackrest(w: number, h: number, d: number): Box[] {
    return [
        box(vec(0, h * 0.12, 0), vec(w, h * 0.24, d * 0.7)),
        box(vec(0, h * 0.5, d * 0.23), vec(w, h * 0.8, d * 0.24))
    ];
}

function regularSeatParts(w: number, h: number, d: number, armrests: { left: boolean; right: boolean }): Box[] {
    const parts = baseAndBackrest(w, h, d);
    const armThick = Math.min(w, d) * 0.18;
    const armHeight = h * 0.25;
    const armY = h * 0.25 + armHeight / 2;
    const armX = w / 2 - armThick / 2;
    const armSize = vec(armThick, armHeight, d * 0.6);
    if (armrests.left) parts.push(box(vec(-armX, armY, -d * 0.05), armSize));
    if (armrests.right) parts.push(box(vec(armX, armY, -d * 0.05), armSize));
    return parts;
}

const WHEEL_RADIUS_M = 0.3;
const FRAME_TUBE_M = 0.028;
/** How far the backrest leans back from vertical, in radians. */
const BACKREST_RECLINE = 0.12;

/**
 * A wheelchair space: a wheelchair standing in it, facing the screen. Cushioned parts (seat, backrest,
 * armrests) are in the seat's color, the frame (rails, posts, push handles, fork, footrest) in the shell
 * material. The two large drive wheels sit at the back and the small casters at the front; every wheel rests
 * on the seat's base, which is the floor of its own tier (the base is where the platform under the seat ends,
 * not the ground of the room). The chair is sized from the space, so a shallow space gets smaller wheels.
 */
export function wheelchairParts(w: number, h: number, d: number): { parts: Box[]; wheels: Wheel[] } {
    const radius = Math.min(WHEEL_RADIUS_M, h * 0.3, d * 0.45);
    const casterRadius = radius * 0.3;
    const half = Math.min(w * 0.72, 0.64) / 2;
    const tube = FRAME_TUBE_M;
    const seatY = h * 0.48;
    const railY = seatY - 0.05;
    const backTop = h * 0.9;
    const wheelZ = d / 2 - radius;
    const backZ = wheelZ + radius * 0.35;
    const casterZ = -d / 2 + casterRadius;
    const seatFront = casterZ + 0.05;
    const seatZ = (seatFront + backZ) / 2;
    const seatDepth = backZ - seatFront;
    const footY = casterRadius * 2 + 0.04;
    const shell = (center: Vec3, size: Vec3): Box => ({ center, size, material: 'shell' });
    const bothSides = (make: (x: number) => Box): Box[] => [make(-1), make(1)];
    const parts: Box[] = [
        box(vec(0, seatY, seatZ), vec(half * 2, 0.07, seatDepth)),
        {
            center: vec(0, (seatY + backTop) / 2, backZ),
            size: vec(half * 2, backTop - seatY, 0.05),
            tiltX: BACKREST_RECLINE
        },
        ...bothSides((x) => box(vec(x * (half - 0.02), seatY + 0.22, seatZ), vec(0.05, 0.03, seatDepth * 0.7))),
        ...bothSides((x) => shell(vec(x * half, railY, seatZ), vec(tube, tube, seatDepth + 0.05))),
        ...bothSides((x) =>
            shell(vec(x * half, (railY + backTop + 0.05) / 2, backZ + 0.02), vec(tube, backTop + 0.05 - railY, tube))
        ),
        ...bothSides((x) => shell(vec(x * half, backTop + 0.05, backZ + 0.07), vec(tube, tube, 0.12))),
        ...bothSides((x) => shell(vec(x * half, seatY + 0.11, seatZ), vec(tube, 0.22, tube))),
        ...bothSides((x) =>
            shell(
                vec(x * half * 0.85, (railY + casterRadius * 2) / 2, casterZ),
                vec(tube, railY - casterRadius * 2, tube)
            )
        ),
        ...bothSides((x) =>
            shell(vec(x * half * 0.55, (railY + footY) / 2, casterZ - 0.02), vec(tube, railY - footY, tube))
        ),
        shell(vec(0, footY, casterZ - 0.03), vec(half * 1.24, 0.02, 0.1)),
        shell(vec(0, radius, wheelZ), vec(half * 2, tube, tube))
    ];
    return {
        parts,
        wheels: [
            { center: vec(-(half + 0.045), radius, wheelZ), radius },
            { center: vec(half + 0.045, radius, wheelZ), radius },
            { center: vec(-half * 0.85, casterRadius, casterZ), radius: casterRadius, caster: true },
            { center: vec(half * 0.85, casterRadius, casterZ), radius: casterRadius, caster: true }
        ]
    };
}

/**
 * Pair halves (loveseats, beds) in the same row with no other seat between them; nearest match of the same
 * kind wins.
 */
/**
 * Which seats an ambience may seat figures on, as a stable `1,2,3` signature (sorted ids; empty when none). The
 * binding rule: only plain seats that are taken (`isLocked`: sold or held, which is how the cart reports them),
 * never a free seat, never the seat the camera sits on (`viewSeatId`). A free accessible ('disabled' type) seat is
 * not selectable by type yet free, so selectability says nothing about being taken. A string,
 * so a lock-poll tick that hands over fresh seat objects with the same set does not rebuild the figures.
 */
export function occupiableSeatIds(
    nodes: readonly Pick<SeatNode, 'seatId' | 'kind' | 'isSelectable'>[],
    seatsById: ReadonlyMap<Seat['id'], Pick<Seat, 'isLocked'>>,
    viewSeatId: Seat['id'] | null
): string {
    return nodes
        .filter(
            (node) =>
                node.kind === 'seat' && node.seatId !== viewSeatId && seatsById.get(node.seatId)?.isLocked === true
        )
        .map((node) => node.seatId)
        .sort((a, b) => a - b)
        .join(',');
}

export function findPairSeats(seats: Seat[]): { leftSeatId: Seat['id']; rightSeatId: Seat['id']; kind: PairKind }[] {
    const rows = new Map<string, { seat: Seat; type: string; x: number }[]>();
    for (const seat of seats) {
        const row = rows.get(seat.rowName) ?? [];
        row.push({ seat, type: seat.type, x: seat.position.x });
        rows.set(seat.rowName, row);
    }
    return [...rows.values()].flatMap((row) =>
        findRowPairs(row, { nonPairsBlock: true }).map(({ left, right, kind }) => ({
            leftSeatId: left.seat.id,
            rightSeatId: right.seat.id,
            kind
        }))
    );
}

/**
 * How a room that went through the Room Studio differs from the plain map. A room without a saved
 * design is built with no options and keeps today's output bit for bit.
 */
export type SceneLayoutOptions = {
    /** Seat silhouette from the room design; omitted = the original seat. */
    seatStyle?: SeatStyle;
    /** The studio stores a screen's positionZ as its center height above the floor; the plain map as world y. */
    screenAboveFloor?: boolean;
    /**
     * Room size in mm: a designed room without a screen object gets the studio's virtual screen, sized from it (from
     * the screen wall of its outline, when it has one).
     */
    room?: { width: number; height: number; geometry?: RoomGeometry | null };
    /** The design's seat settings (pose, two materials, panels, recliner extras); omitted = plain seats. */
    seatLook?: SeatLook;
    /** The room default form of a designed room (`roomSeatForm`); it wins over `seatStyle` and `seatLook`. */
    seatForm?: SeatForm;
    /** The seats whose form differs from the room default (`resolveSeatForms`), by seat id. */
    seatForms?: ReadonlyMap<Seat['id'], SeatForm>;
};

function screenFromRoomObject(screen: RoomObject, options: SceneLayoutOptions): ScreenNode {
    const transform = screen.dto._embedded?.transform;
    const w = screen.width / MM;
    const h = screen.height / MM;
    const rawZ = transform?.positionZ ?? 0;
    const floor = options.screenAboveFloor ? GROUND_Y : 0;
    const profile = normalizeObjectProfile(screen.dto._embedded?.shape?.profile);
    return {
        center: vec(
            (transform?.positionX ?? 0) / MM,
            rawZ === 0 ? floor + h / 2 + h * 0.3 : floor + rawZ / MM,
            -(transform?.positionY ?? 0) / MM
        ),
        size: vec(w, h, screen.depth / MM),
        ...(profile?.bezel || profile?.curve ? { profile } : {})
    };
}

const VIRTUAL_SCREEN_DEPTH_M = 0.1;

/** The screen a designed room shows when it has none of its own, on the wall like in the studio. */
function virtualScreen(
    seats: Seat[],
    room: { width: number; height: number; geometry?: RoomGeometry | null }
): ScreenNode {
    const f = designedFallbackScreen(
        room,
        seats.map((s) => ({ x: s.position.x, y: s.position.y }))
    );
    // The virtual screen's face is on the wall; its box sits behind it, as the studio draws it.
    return {
        center: vec(f.x / MM, GROUND_Y + (f.bottom + f.height / 2) / MM, -f.y / MM - VIRTUAL_SCREEN_DEPTH_M / 2),
        size: vec(f.width / MM, f.height / MM, VIRTUAL_SCREEN_DEPTH_M)
    };
}

function fallbackScreen(seats: Seat[]): ScreenNode {
    if (seats.length === 0) return { center: vec(0, 1, -1), size: vec(16 / 4.5, 2, 0.1) };
    const xs = seats.map((s) => s.position.x);
    const ys = seats.map((s) => s.position.y);
    const minDepth = Math.min(...seats.map((s) => s.depth));
    const roomWidth = (Math.max(...xs) - Math.min(...xs)) / MM;
    const roomDepth = (Math.max(...ys) - Math.min(...ys)) / MM;
    const gap = Math.max(roomDepth * 0.5, (minDepth * 5) / MM);
    const w = Math.max(roomWidth * 0.85, (minDepth * 10) / MM);
    const h = w / (16 / 9);
    const frontZ = -Math.max(...ys) / MM;
    return { center: vec((Math.min(...xs) + Math.max(...xs)) / 2 / MM, h / 2, frontZ - gap), size: vec(w, h, 0.1) };
}

export function buildSceneLayout(
    seats: Seat[],
    roomObjects: RoomObject[] = [],
    options: SceneLayoutOptions = {}
): SceneLayout {
    const heights = seats.map((s) => s.position.z);
    const minHeight = heights.length ? Math.min(...heights) : 0;
    const range = heights.length ? Math.max(...heights) - minHeight : 0;
    const heightScale = range > 0 ? MAX_TIER_HEIGHT / range : 0;
    // A designed room shows seat heights the way the studio drew them: z is the literal elevation in mm.
    // Only an implausible range (ticket systems store row numbers as heights) is squeezed into tiers.
    const literalHeights = options.screenAboveFloor === true;
    // With balconies each floor measures the heights from its own elevation, so a balcony at 4.5 m is literal.
    const geometry = options.room?.geometry;
    const seatRise =
        literalHeights && geometry?.levels?.length
            ? resolveSeatRise(
                  seats.map((s) => ({ x: s.position.x, y: s.position.y, z: s.position.z })),
                  geometry
              )
            : null;
    const pedestalOf = (s: Seat) =>
        seatRise
            ? seatRise({ x: s.position.x, y: s.position.y, z: s.position.z }) / MM
            : literalHeights
              ? range > MAX_PLAUSIBLE_RISE_MM
                  ? (s.position.z - minHeight) * heightScale
                  : Math.max(0, s.position.z) / MM
              : PEDESTAL_BASE + (s.position.z - minHeight) * heightScale;

    const pairs = findPairSeats(seats);
    const pairedSide = new Map<Seat['id'], 'left' | 'right'>();
    for (const p of pairs) {
        pairedSide.set(p.leftSeatId, 'left');
        pairedSide.set(p.rightSeatId, 'right');
    }

    const formOf = (id: Seat['id']): SeatForm | null => options.seatForms?.get(id) ?? options.seatForm ?? null;
    const anyEndPanels =
        options.seatLook?.endPanels === true ||
        options.seatForm?.endPanels === true ||
        [...(options.seatForms?.values() ?? [])].some((form) => form.endPanels);
    const ends = anyEndPanels
        ? seatEndPanels(seats.map((s) => ({ key: s.id, rowName: s.rowName, x: s.position.x, width: s.width })))
        : null;
    const styleOf = (form: SeatForm | null): SeatStyle | undefined => form?.style ?? options.seatStyle;
    const lookOf = (form: SeatForm | null): SeatLook | undefined => (form ? seatLookOfForm(form) : options.seatLook);
    const seatModel = (
        seat: Seat,
        form: SeatForm | null,
        w: number,
        h: number,
        d: number,
        armrests: { left: boolean; right: boolean }
    ): { parts: Box[]; glows: Box[] } => {
        const style = styleOf(form);
        const look = lookOf(form);
        return style
            ? seatStyleModel(style, w, h, d, armrests, look ? seatOptionsFor(look, ends?.get(seat.id)) : undefined)
            : { parts: regularSeatParts(w, h, d, armrests), glows: [] };
    };

    const nodes: SeatNode[] = seats.map((s) => {
        const w = s.width / MM;
        const h = s.height / MM;
        const d = s.depth / MM;
        const pedestal = pedestalOf(s);
        const isWheelchair = WHEELCHAIR_TYPES.has(s.type);
        const furniture = isWheelchair ? null : furnitureKindOf(s.type);
        const side = pairedSide.get(s.id);
        const sides = { left: side !== 'right', right: side !== 'left' };
        const built = furniture ? furnitureParts(furniture, w, h, d, sides) : null;
        const form = formOf(s.id);
        const styled = isWheelchair || built ? null : seatModel(s, form, w, h, d, sides);
        const style = styleOf(form);
        const look = lookOf(form);
        const eyeOffset = style && look ? seatEyeOffset(style, look.pose) : null;
        const { parts, wheels } = isWheelchair
            ? wheelchairParts(w, h, d)
            : { parts: built ? built.parts : (styled?.parts ?? []), wheels: [] };
        const glows = built ? built.glows : (styled?.glows ?? []);
        return {
            seatId: s.id,
            kind: isWheelchair ? 'wheelchair' : (furniture ?? 'seat'),
            isSelectable: !NON_SELECTABLE_SEAT_TYPES.has(s.type),
            ...(s.isDbox && !isWheelchair ? { isDbox: true } : {}),
            position: vec(s.position.x / MM, GROUND_Y + pedestal, -s.position.y / MM),
            rotationY: -(s.rotation.z * Math.PI) / 180,
            size: vec(w, h, d),
            parts,
            wheels,
            ...(glows.length > 0 ? { glows } : {}),
            ...(eyeOffset && !isWheelchair && !built ? { eyeOffset } : {}),
            ...(form ? { form } : {})
        };
    });

    const byId = new Map(seats.map((s) => [s.id, s]));
    const bridges: BridgeNode[] = [];
    for (const p of pairs) {
        const left = byId.get(p.leftSeatId);
        const right = byId.get(p.rightSeatId);
        if (!left || !right) continue;
        const leftEdge = (left.position.x + left.width / 2) / MM;
        const rightEdge = (right.position.x - right.width / 2) / MM;
        const width = rightEdge - leftEdge;
        if (width <= 0) continue;
        const h = left.height / MM;
        const d = left.depth / MM;
        const bed = p.kind === 'bed' ? bedBridgeParts(width, h, d) : null;
        const form = formOf(left.id);
        const bridgeStyle = styleOf(form);
        const bridgeLook = lookOf(form);
        bridges.push({
            leftSeatId: left.id,
            rightSeatId: right.id,
            position: vec((leftEdge + rightEdge) / 2, GROUND_Y + pedestalOf(left), -left.position.y / MM),
            rotationY: -(left.rotation.z * Math.PI) / 180,
            parts: bed
                ? bed.parts
                : bridgeStyle
                  ? seatStyleBridgeParts(
                        bridgeStyle,
                        width,
                        h,
                        d,
                        bridgeLook ? { pose: bridgeLook.pose, twoTone: bridgeLook.twoTone } : {}
                    )
                  : baseAndBackrest(width, h, d),
            ...(bed ? { glows: bed.glows } : {}),
            ...(form ? { form } : {})
        });
    }

    const screenObject = roomObjects.find((o) => o.type === 'screen');
    const screen = screenObject
        ? screenFromRoomObject(screenObject, options)
        : options.screenAboveFloor && options.room
          ? virtualScreen(seats, options.room)
          : fallbackScreen(seats);

    const xs = [
        ...nodes.map((n) => n.position.x),
        screen.center.x - screen.size.x / 2,
        screen.center.x + screen.size.x / 2
    ];
    const ys = [GROUND_Y, ...nodes.map((n) => n.position.y + n.size.y), screen.center.y + screen.size.y / 2];
    const zs = [...nodes.map((n) => n.position.z), screen.center.z];
    const min = vec(Math.min(...xs), Math.min(...ys), Math.min(...zs));
    const max = vec(Math.max(...xs), Math.max(...ys), Math.max(...zs));
    const center = vec((min.x + max.x) / 2, (min.y + max.y) / 2, (min.z + max.z) / 2);

    return { seats: nodes, bridges, screen, bounds: { min, max, center }, groundY: GROUND_Y };
}

/** A compact key of an object's profile (empty for a plain box); the profile is untrusted JSON of bounded size. */
function profileKey(profile: unknown): string {
    return profile === undefined || profile === null ? '' : JSON.stringify(profile);
}

/**
 * A string that changes if and only if `buildSceneLayout`'s output would change: seat/bridge
 * positions, rotations, shapes, types and row names (`rowName` feeds `findPairSeats`'
 * armrest-suppression and bridge placement, so it is structural too), and room-object placement and
 * shape. Deliberately leaves out mutable per-booking seat state (`isSelected`, `isLocked`, `isError`,
 * `isProcessing`) so a caller that rebuilds its `seats` array on every lock-poll tick or selection
 * toggle -- as `SeatSelection.tsx`'s cart merge does, via `seatsProp.map((seat) => ({ ...seat, ... }))`
 * -- can memoize the 3D mesh geometry and camera pose on this key instead of on array identity, and
 * only rebuild them when the room's actual shape changes.
 */
export function sceneGeometryKey(
    seats: Seat[],
    roomObjects: RoomObject[] = [],
    options: SceneLayoutOptions = {}
): string {
    const seatPart = seats
        .map((s) =>
            [
                s.id,
                s.type,
                s.rowName,
                s.position.x,
                s.position.y,
                s.position.z,
                s.rotation.x,
                s.rotation.y,
                s.rotation.z,
                s.width,
                s.height,
                s.depth,
                s.isDbox ? 'dbox' : ''
            ].join(',')
        )
        .join('|');
    const roomObjectPart = roomObjects
        .map((o) => {
            const t = o.dto._embedded?.transform;
            return [
                o.id,
                o.type ?? '',
                o.width,
                o.height,
                o.depth,
                t?.positionX ?? '',
                t?.positionY ?? '',
                t?.positionZ ?? '',
                t?.rotationX ?? '',
                t?.rotationY ?? '',
                t?.rotationZ ?? '',
                // A curved outline changes the object's look without changing its box.
                profileKey(o.dto._embedded?.shape?.profile)
            ].join(',');
        })
        .join('|');
    const formPart = options.seatForm
        ? `:${fullFormKey(options.seatForm)}${[...(options.seatForms ?? [])].map(([id, form]) => `;${id}=${fullFormKey(form)}`).join('')}`
        : '';
    const lookPart = `${options.seatLook ? `:${JSON.stringify(options.seatLook)}` : ''}${formPart}`;
    const optionPart =
        options.seatStyle || options.screenAboveFloor
            ? `::${options.seatStyle ?? ''}:${options.screenAboveFloor ? 1 : 0}:${options.room ? `${options.room.width}x${options.room.height}${options.room.geometry?.outline ? `s${screenWallWidth(options.room)}` : ''}` : ''}${lookPart}`
            : '';
    return `${seatPart}::${roomObjectPart}${optionPart}`;
}
